import React, { FunctionComponent } from 'react';

import { SnackbarStack } from '@yandex-360/orb-components/dist/components/Snackbar';
import cn from 'classnames';
import { useDispatch, useSelector } from 'react-redux';

import { NotificationId } from '../../../../../../pages/call/types';
import Button, { ButtonProps } from '../../../../../components/button';
import { IconostasisImage } from '../../../../../components/iconostasis-image/iconostasis-image';
import ParticipantIcon from '../../../../../components/participant-icon';
import { Snackbar } from '../../../../../components/snackbar';
import SvgImage from '../../../../../components/svg-image';
import { ImageName } from '../../../../../components/svg-image/images';
import { useTypedSelector } from '../../../../../hooks/useTypedSelector';
import { selectIsMeetingRoomIntegration } from '../../../../config/selectors';
import { getActionOnClick } from '../../actions';
import { MEETING_ROOM_ALLOWED_NOTIFICATIONS } from '../../constants';
import { hideNotification } from '../../slice';
import { ExtendedNotificationItem, NotificationType } from '../../types';
import styles from './styles.css';

const CENTERED_NOTIFICATIONS: NotificationId[] = [NotificationId.ONLINE];
const MODERATION_NOTIFICATIONS = Object.entries(NotificationId)
    .filter(([key]) => key.startsWith('MODERATION_'))
    .map(([, value]) => value);

const WAITING_ROOM_NOTIFICATIONS = [
    NotificationId.WAITING_ROOM_NEW_PEER,
    NotificationId.WAITING_ROOM_NEW_PEERS,
    NotificationId.WAITING_ROOM_OVERFLOW,
];

type NotificationsListProps = {
    notifications: ExtendedNotificationItem[];
};

export const NotificationsList: FunctionComponent<NotificationsListProps> = ({ notifications }) => {
    const dispatch = useDispatch();
    const sessionId = useTypedSelector((state) => state.sessionId);
    const isMeetingRoomIntegration = useSelector(selectIsMeetingRoomIntegration);

    return (
        <SnackbarStack align="center">
            {notifications.map((notification) => {
                if (
                    isMeetingRoomIntegration &&
                    !MEETING_ROOM_ALLOWED_NOTIFICATIONS.includes(notification.id as NotificationId)
                ) {
                    return null;
                }

                const isWaitingRoomNotification = WAITING_ROOM_NOTIFICATIONS.includes(
                    notification.id as NotificationId
                );
                const isCustomCloseButton =
                    isWaitingRoomNotification ||
                    [
                        NotificationId.CONF_HISTORY_FETCHING_FAILED,
                        NotificationId.REPORT_ERROR,
                        NotificationId.TELE_MESSENGER_LINK_COPIED,
                    ].includes(notification.id as NotificationId);
                const isError = notification.type === NotificationType.ERROR;
                const isSuccess = notification.type === NotificationType.SUCCESS;
                const isTypeWithIcon = isError || isSuccess;
                const texts = Array.isArray(notification.text) ? notification.text : [notification.text];
                const content = texts.map((text, index) => (
                    <div key={index} className={styles.text}>
                        {text}
                    </div>
                ));
                let icon;

                let contentStyle = '';
                if (MODERATION_NOTIFICATIONS.includes(notification.id as NotificationId) && notification.participant) {
                    icon = (
                        <ParticipantIcon
                            width={40}
                            height={40}
                            className={styles.image}
                            participantId={notification.participant.id}
                        />
                    );
                } else if (notification.id === NotificationId.WAITING_ROOM_NEW_PEER && notification.waitingRoomPeer) {
                    icon = (
                        <ParticipantIcon
                            width={32}
                            height={32}
                            className={styles.image}
                            participantId={notification.waitingRoomPeer.id}
                        />
                    );
                } else if (notification.iconostasisIcon) {
                    icon = (
                        <div className={styles.iconostasisIconWrapper}>
                            <IconostasisImage {...notification.iconostasisIcon} />
                        </div>
                    );
                } else if (notification.icon && isWaitingRoomNotification) {
                    icon = (
                        <div className={styles.iconWrapper}>
                            <SvgImage noContainer imageName={notification.icon as ImageName} className={styles.icon} />
                        </div>
                    );
                } else if (notification.icon) {
                    const isSpinner = notification.icon === 'spinner';
                    icon = (
                        <SvgImage
                            imageName={notification.icon as ImageName}
                            className={cn(styles.icon, {
                                [styles.spinner]: isSpinner,
                            })}
                        />
                    );
                } else if (isTypeWithIcon) {
                    icon = (
                        <SvgImage
                            imageName={notification?.type?.toLowerCase?.() as ImageName}
                            className={cn(styles.icon, {
                                [styles.iconSuccess]: isSuccess,
                                [styles.iconError]: isError,
                            })}
                        />
                    );
                }

                if (CENTERED_NOTIFICATIONS.includes(notification.id as NotificationId)) {
                    contentStyle = cn(contentStyle, styles.centered);
                }

                const actions = notification.actions?.map((action, index) => (
                    <Button
                        key={index}
                        view={(action.buttonView || 'neutral') as ButtonProps['view']}
                        size="sm"
                        elementType="a"
                        href={action.url}
                        target="_blank"
                        onClick={getActionOnClick(notification.id, sessionId, action.params)}
                        className={styles.button}
                    >
                        {action.text}
                    </Button>
                ));

                return (
                    <Snackbar
                        key={notification.id}
                        className={cn(styles.snackbar, {
                            [styles.withButtons]: Boolean(actions?.length),
                            [styles.waitingRoomNotification]: isWaitingRoomNotification,
                            [styles.iconFixedSize]: notification.iconFixedSize,
                        })}
                        startSlot={icon}
                        actions={actions?.length && !isMeetingRoomIntegration ? actions : null}
                        hasClose={!!(notification.withCloseButton && isCustomCloseButton && actions)}
                        visible={true}
                        onClose={() => dispatch(hideNotification(notification.id))}
                    >
                        <div className={contentStyle}>{content}</div>
                    </Snackbar>
                );
            })}
        </SnackbarStack>
    );
};
